import { useToast } from '@facephi/ui-react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  endPoints,
  newOperationIdState,
  STATUS_CODE_200,
  WEB_SOURCE,
} from '../states';
import { RequestMethods, useRequest } from './useRequest';
import { useVariables } from './useVariables';

export function useSendAsset() {
  const { t } = useTranslation();
  const { toastManager } = useToast();
  const { apiDemoUrl } = useVariables();
  const { request: assetsRequest } = useRequest(apiDemoUrl);
  const newOperationId = useRecoilValue(newOperationIdState);
  const headers = {
    'x-source': WEB_SOURCE,
    'x-operation-id': newOperationId,
  };

  const toastError = () => {
    toastManager({
      type: 'error',
      message: t('Error sending asset'),
      duration: 2000,
    });
  };

  const registerSelfie = async (selfie?: string): Promise<boolean> =>
    assetsRequest(`${endPoints.Assets.Register}${endPoints.Assets.Selfie}`, {
      method: RequestMethods.post,
      headers,
      data: {
        selfie,
      },
    })
      .then(
        (response) => response.succeeded && response.status === STATUS_CODE_200
      )
      .catch((error: AxiosError) => {
        toastError();
        throw new Error(error.message);
      });

  const registerAudio = async (audios: string[]): Promise<string> =>
    assetsRequest(`${endPoints.Assets.Register}${endPoints.Assets.Audio}`, {
      method: RequestMethods.post,
      headers,
      data: {
        audios,
      },
    })
      .then((response) => response)
      .catch((error: AxiosError) => {
        toastError();
        throw new Error(error.message);
      });

  const authenticateSelfie = async (
    registeredSelfie?: string,
    selfie?: string
  ): Promise<boolean> =>
    assetsRequest(
      `${endPoints.Assets.Authenticate}${endPoints.Assets.Selfie}`,
      {
        method: RequestMethods.post,
        headers,
        data: {
          selfie,
          registeredSelfie,
        },
      }
    )
      .then(
        (response) => response.succeeded && response.status === STATUS_CODE_200
      )
      .catch((error: AxiosError) => {
        toastError();
        throw new Error(error.message);
      });

  const authenticateAudio = async (
    audio: string,
    template: string
  ): Promise<string> =>
    assetsRequest(`${endPoints.Assets.Authenticate}${endPoints.Assets.Audio}`, {
      method: RequestMethods.post,
      headers,
      data: {
        audio,
        template,
      },
    })
      .then((response) => response)
      .catch((error: AxiosError) => {
        toastError();
        throw new Error(error.message);
      });

  return {
    registerSelfie,
    registerAudio,
    authenticateSelfie,
    authenticateAudio,
  };
}
